import { Observable } from 'rxjs';
import { InjectionToken } from '../../injection';
import { AdminDomainMembership, AdminDomainMembershipEagerLoad } from '../data/admin-domain-membership.do';
import { AdminDomain } from '../data/admin-domain.do';
import { EmptyResponse } from '../data/empty-response.do';
import { ErpCredentials } from '../data/erp-credentials.do';
import { MobizEndpoint } from '../data/mobiz-endpoint.do';
import { PagedResults } from '../data/paged-results.model';
import { Dictionary, ParameterValueType } from '../interfaces/dictionary.interface';
import { FlagsEnum } from '../other/flags-enum';

export type AdminDomainStatusFilter = 'Active' | 'Inactive' | 'All';

/**
 * Possible query parameters for fetching Admin Domains.
 *  @param page Optional page index.
 *  @param pageSize Optional number of items per page.
 *  @param filter Optional search string.
 */
export interface AdminDomainQueryParameters extends Dictionary<ParameterValueType> {
  page?: number;
  pageSize?: number;
  filter?: string;
  status?: AdminDomainStatusFilter;
}

/**
 * Possible query parameters for fetching Admin Domains.
 *  @param adminDomainId Required admin domain identifier.
 *  @param page Optional page index.
 *  @param pageSize Optional number of items per page.
 *  @param filter Optional search string.
 */
export interface EndpointQueryParameters extends Dictionary<ParameterValueType> {
  adminDomainId: string;
  page?: number;
  pageSize?: number;
  filter?: string;
}

/**
 * Possible query parameters for fetching Admin Domain Memberships.
 *  @param adminDomainId Required admin domain identifier.
 *  @param page Optional page index.
 *  @param pageSize Optional number of items per page.
 */
export interface AdminDomainMembershipQueryParameters extends Dictionary<ParameterValueType> {
  adminDomainId: string;
  page?: number;
  pageSize?: number;
}

export interface IAdminDomainService {
  /**
   * Creates a new Admin Domain.
   * @param adminDomain The admin domain to create.
   */
  createAdminDomain(adminDomain: AdminDomain): Observable<AdminDomain>;

  /**
   * Returns an existing Admin Domain.
   * @param adminDomainId The admin domain identifier.
   */
  getAdminDomain(adminDomainId: string): Observable<AdminDomain>;

  /**
   * Retrieves multiple Admin Domains.
   * @param queryParameters Dictionary containing the query parameters.
   */
  getAdminDomains(queryParameters: AdminDomainQueryParameters): Observable<PagedResults<AdminDomain>>;

  /**
   * Updates an existing Admin Domain.
   * @param adminDomain The admin domain to update.
   */
  updateAdminDomain(adminDomain: AdminDomain): Observable<AdminDomain>;

  /**
   * Deletes an existing Admin Domain.
   * @param adminDomainId The admin domain identifier.
   */
  deleteAdminDomain(adminDomainId: string): Observable<EmptyResponse>;

  /**
   * Creates a new Endpoint.
   * @param endpoint The endpoint to create.
   * @param credentials The ERP credentials.
   */
  createEndpoint(endpoint: MobizEndpoint, credentials: ErpCredentials): Observable<MobizEndpoint>;

  /**
   * Retrieves multiple Endpoints.
   * @param queryParameters Dictionary containing the query parameters.
   */
  getEndpoints(queryParameters: EndpointQueryParameters): Observable<PagedResults<MobizEndpoint>>;

  /**
   * Updates a new Endpoint.
   * @param endpoint The endpoint to update.
   * @param credentials The ERP credentials.
   */
  updateEndpoint(endpoint: MobizEndpoint, credentials: ErpCredentials): Observable<MobizEndpoint>;

  /**
   * Deletes an existing Endpoint.
   * @param adminDomainId The admin domain identifier.
   * @param endpointId The endpoint identifier.
   */
  deleteEndpoint(adminDomainId: string, endpointId: string): Observable<EmptyResponse>;

  /**
   * Retrieves multiple admin domain memberships.
   *
   * @param queryParameters Dictionary containing the query parameters.
   */
  getAdminDomainMemberships(
    queryParameters: AdminDomainMembershipQueryParameters,
    eagerLoad?: FlagsEnum<AdminDomainMembershipEagerLoad>
  ): Observable<PagedResults<AdminDomainMembership>>;

  /**
   * Returns an existing Admin Domain Membership.
   * @param adminDomainId The admin domain identifier.
   * @param membershipId The admin domain membership identifier.
   */
  getAdminDomainMembership(adminDomainId: string, membershipId: string): Observable<AdminDomainMembership>;

  /**
   * Creates a new Admin Domain Membership.
   * @param membership The admin domain membership to create.
   */
  createAdminDomainMembership(membership: AdminDomainMembership): Observable<AdminDomainMembership>;

  /**
   * Updates an existing Admin Domain Membership.
   * @param membership The admin domain membership to update.
   */
  updateAdminDomainMembership(membership: AdminDomainMembership): Observable<AdminDomainMembership>;

  /**
   * Deletes an existing Admin Domain Membership.
   * @param adminDomainId The admin domain identifier.
   * @param membershipId The admin domain membership identifier.
   */
  deleteAdminDomainMembership(adminDomainId: string, membershipId: string): Observable<EmptyResponse>;
}
export const IAdminDomainService = new InjectionToken('IAdminDomainService');
